import { Link } from "react-router-dom";
import { ShoppingCart, MessageCircle, MapPin, CheckCircle2, CreditCard, Truck } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { useSEO } from "@/lib/seo";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/store/Reveal";

const STEPS = [
  { icon: ShoppingCart, title: "1. Arma tu carrito", text: "Explora el catálogo y agrega los productos que tu mascota necesita. Si el producto tiene variantes (tallas o presentaciones), selecciónala antes de agregar. Puedes comprar sin registrarte." },
  { icon: MessageCircle, title: "2. Envía tu pedido por WhatsApp", text: "Al finalizar, tu carrito se convierte en un mensaje con el SKU de cada producto. Agrega tu nombre completo, teléfono y dirección: puedes escribirla o (preferiblemente) pegar el link de Waze/Google Maps, o enviarnos tu ubicación por WhatsApp en otro mensaje." },
  { icon: CheckCircle2, title: "3. Confirmamos disponibilidad", text: "Te respondemos para confirmar productos, coordinar la entrega y darte el total final." },
  { icon: CreditCard, title: "4. Paga al recibir en el GAM", text: "Dentro del Gran Área Metropolitana pagas al recibir tu pedido: en efectivo, por SINPE Móvil o transferencia." },
  { icon: Truck, title: "5. Recibe en tu casa", text: "Coordinamos la entrega por WhatsApp. Los envíos fuera del GAM tienen un costo de ₡2.000. ¡Tu mascota disfrutará muy pronto!" },
];

export default function ComoComprar() {
  const { settings } = useSettings();
  useSEO({
    title: "Cómo comprar | The Pet Shopping Co Costa Rica",
    description: "Comprar en The Pet Shopping Co es muy fácil: arma tu carrito, envíalo por WhatsApp con tus datos y coordina entrega y pago. Envíos a todo Costa Rica.",
    canonicalPath: "/como-comprar",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: "Cómo comprar en The Pet Shopping Co",
      step: STEPS.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s.title, text: s.text })),
    },
  });

  return (
    <div className="max-w-4xl mx-auto px-5 pt-12 sm:pt-16 pb-20">
      <Reveal>
        <span className="text-xs font-semibold tracking-[0.25em] uppercase text-coral">Guía rápida</span>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-charcoal tracking-tight mt-3">Cómo comprar</h1>
        <p className="text-muted-moss mt-3 text-lg max-w-2xl">Comprar para tu mascota nunca fue tan fácil. En 5 pasos coordinamos todo por WhatsApp.</p>
      </Reveal>

      <StaggerGroup className="mt-12 space-y-4">
        {STEPS.map((s) => (
          <StaggerItem key={s.title}>
            <div className="flex gap-5 bg-white rounded-3xl border border-[#E3E7E2] p-6 card-shadow">
              <span className="grid place-items-center w-14 h-14 rounded-2xl bg-peach text-coral shrink-0"><s.icon className="w-7 h-7" /></span>
              <div>
                <h2 className="font-display text-xl font-bold text-charcoal">{s.title}</h2>
                <p className="text-charcoal/75 mt-1 leading-relaxed">{s.text}</p>
              </div>
            </div>
          </StaggerItem>
        ))}
      </StaggerGroup>

      <div className="grid sm:grid-cols-2 gap-4 mt-8">
        <Reveal>
          <div className="h-full rounded-3xl bg-white border border-[#E3E7E2] p-6 card-shadow">
            <span className="grid place-items-center w-12 h-12 rounded-2xl bg-peach text-coral mb-4"><CreditCard className="w-6 h-6" /></span>
            <h3 className="font-display text-xl font-bold text-charcoal">Formas de pago</h3>
            <p className="text-charcoal/75 mt-1 leading-relaxed">Dentro del <strong>GAM</strong> pagas <strong>al recibir</strong> tu pedido:</p>
            <ul className="mt-3 space-y-1.5 text-charcoal/80 text-sm">
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-coral" /> Efectivo</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-coral" /> SINPE Móvil</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-coral" /> Transferencia bancaria</li>
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="h-full rounded-3xl bg-white border border-[#E3E7E2] p-6 card-shadow">
            <span className="grid place-items-center w-12 h-12 rounded-2xl bg-peach text-coral mb-4"><Truck className="w-6 h-6" /></span>
            <h3 className="font-display text-xl font-bold text-charcoal">Envíos</h3>
            <p className="text-charcoal/75 mt-1 leading-relaxed">Coordinamos la entrega por WhatsApp según tu zona.</p>
            <div className="mt-3 rounded-2xl bg-teal/25 border border-teal/50 p-3 text-sm text-charcoal">
              <p><strong>Dentro del GAM:</strong> entrega coordinada por WhatsApp.</p>
              <p className="mt-1"><strong>Fuera del GAM:</strong> ₡2.000 de envío, también coordinado por WhatsApp.</p>
            </div>
          </div>
        </Reveal>
      </div>

      <Reveal className="mt-12">
        <div className="rounded-3xl bg-teal/25 border border-teal/50 p-6 sm:p-8 text-center">
          <MapPin className="w-8 h-8 mx-auto text-coral mb-3" />
          <h3 className="font-display text-2xl font-bold text-charcoal">Datos que debes enviar</h3>
          <p className="text-charcoal/75 mt-2 max-w-lg mx-auto">Para agilizar tu pedido incluye siempre tu <strong>nombre completo</strong> y <strong>teléfono</strong>. La <strong>dirección</strong> puedes escribirla o, de preferencia, pegar el <strong>link de Waze/Google Maps</strong>, o enviarnos tu <strong>ubicación por WhatsApp</strong> en otro mensaje.</p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/tienda" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-charcoal text-cream font-semibold hover:bg-coral transition-colors">Ir a la tienda</Link>
            <a href={`https://wa.me/${settings.whatsapp_number}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-charcoal font-semibold hover:bg-cream-2 transition-colors"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
          </div>
        </div>
      </Reveal>
    </div>
  );
}
